import { useEffect, useMemo, useState } from "react";
import { useServerFn } from "@tanstack/react-start";
import { toast } from "sonner";
import { Mail, MessageCircle, Check } from "lucide-react";
import { parsePhoneNumberFromString, type CountryCode } from "libphonenumber-js";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { requestGuide } from "@/lib/leads.functions";
import guideCover from "@/assets/guide/guide-cover.jpg";

/** Zentrale WhatsApp-Nummer von SalonTurbo (E.164 ohne +). */
const WHATSAPP_TARGET = "15554516656";

const COUNTRIES: { code: CountryCode; dial: string; flag: string; label: string }[] = [
  { code: "DE", dial: "+49", flag: "🇩🇪", label: "Deutschland" },
  { code: "AT", dial: "+43", flag: "🇦🇹", label: "Österreich" },
  { code: "CH", dial: "+41", flag: "🇨🇭", label: "Schweiz" },
  { code: "NL", dial: "+31", flag: "🇳🇱", label: "Niederlande" },
  { code: "BE", dial: "+32", flag: "🇧🇪", label: "Belgien" },
  { code: "LU", dial: "+352", flag: "🇱🇺", label: "Luxemburg" },
  { code: "IT", dial: "+39", flag: "🇮🇹", label: "Italien" },
  { code: "FR", dial: "+33", flag: "🇫🇷", label: "Frankreich" },
  { code: "PL", dial: "+48", flag: "🇵🇱", label: "Polen" },
  { code: "ES", dial: "+34", flag: "🇪🇸", label: "Spanien" },
  { code: "GB", dial: "+44", flag: "🇬🇧", label: "Großbritannien" },
];

function detectCountry(): CountryCode {
  try {
    const candidates = [
      ...(navigator.languages ?? [navigator.language]),
      Intl.DateTimeFormat().resolvedOptions().locale,
    ].filter(Boolean) as string[];
    for (const tag of candidates) {
      const region = tag.split("-")[1]?.toUpperCase();
      const hit = COUNTRIES.find((c) => c.code === region);
      if (hit) return hit.code;
    }
  } catch {
    /* SSR / eingeschränkte Umgebung */
  }
  return "DE";
}

export function GuideCard() {
  const submit = useServerFn(requestGuide);
  const [channel, setChannel] = useState<"whatsapp" | "email">("whatsapp");
  const [country, setCountry] = useState<CountryCode>("DE");
  const [phone, setPhone] = useState("");
  const [email, setEmail] = useState("");
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState<null | "whatsapp" | "email">(null);

  useEffect(() => {
    setCountry(detectCountry());
  }, []);

  const activeCountry = COUNTRIES.find((c) => c.code === country) ?? COUNTRIES[0]!;

  const parsedPhone = useMemo(() => {
    if (!phone.trim()) return null;
    const parsed = parsePhoneNumberFromString(phone, country);
    return parsed && parsed.isValid() ? parsed : null;
  }, [phone, country]);

  const phoneError = phone.trim().length > 3 && !parsedPhone;

  async function sendWhatsapp() {
    if (!parsedPhone) {
      toast.error("Bitte eine gültige Mobilnummer eingeben.");
      return;
    }
    const e164 = parsedPhone.number;
    setBusy(true);
    try {
      const result = await submit({ data: { contact: e164, channel: "whatsapp" } });
      if (!result.ok) throw new Error("failed");
      const text = encodeURIComponent(
        `Hallo SalonTurbo, bitte schickt mir den kostenfreien Guide „Die 3-Tage-Regel“. Meine Nummer: ${e164}`,
      );
      window.open(`https://wa.me/${WHATSAPP_TARGET}?text=${text}`, "_blank", "noopener,noreferrer");
      setDone("whatsapp");
      toast.success("WhatsApp öffnet sich – Nachricht nur noch abschicken.");
    } catch {
      toast.error("Hat nicht geklappt. Bitte später erneut versuchen.");
    } finally {
      setBusy(false);
    }
  }

  async function sendEmail() {
    const value = email.trim();
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/.test(value)) {
      toast.error("Bitte eine gültige E-Mail-Adresse eingeben.");
      return;
    }
    setBusy(true);
    try {
      const result = await submit({ data: { contact: value, channel: "email" } });
      if (!result.ok) throw new Error("failed");
      setDone("email");
      setEmail("");
      toast.success("Danke! Wir schicken dir den Guide.");
    } catch {
      toast.error("Hat nicht geklappt. Bitte später erneut versuchen.");
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="overflow-hidden rounded-3xl border border-primary/20 bg-background text-foreground shadow-soft-lg">
      <div className="grid gap-0 sm:grid-cols-[minmax(0,150px)_1fr]">
        <div className="hidden bg-secondary/60 sm:block">
          <img
            src={guideCover}
            alt="Titelseite des Guides „Die 3-Tage-Regel“"
            className="h-full w-full object-cover"
          />
        </div>

        <div className="p-6 sm:p-8">
          <p className="eyebrow text-primary">Kostenloser Guide · 16 Seiten PDF</p>
          <h2 className="mt-3 font-display text-2xl font-normal leading-snug sm:text-3xl">
            Die 3-Tage-Regel: kurzfristige Ausfälle füllen – ohne Rabatte
          </h2>
          <p className="mt-3 text-sm leading-relaxed text-muted-foreground">
            Wähle, wie du den Guide bekommen möchtest.
          </p>

          <div className="mt-5 inline-flex rounded-2xl border bg-secondary/50 p-1">
            {[
              { key: "whatsapp" as const, icon: MessageCircle, label: "WhatsApp" },
              { key: "email" as const, icon: Mail, label: "E-Mail" },
            ].map((option) => {
              const active = channel === option.key;
              return (
                <button
                  key={option.key}
                  type="button"
                  aria-pressed={active}
                  onClick={() => setChannel(option.key)}
                  className={`flex items-center gap-2 rounded-xl px-4 py-2 text-sm font-medium transition-colors ${
                    active
                      ? "bg-background text-foreground shadow-soft"
                      : "text-muted-foreground hover:text-foreground"
                  }`}
                >
                  <option.icon className="size-4" />
                  {option.label}
                </button>
              );
            })}
          </div>

          {channel === "whatsapp" ? (
            <form
              className="mt-5"
              onSubmit={(event) => {
                event.preventDefault();
                void sendWhatsapp();
              }}
            >
              <label htmlFor="guide-phone" className="sr-only">
                Mobilnummer für WhatsApp
              </label>
              <div className="flex flex-col gap-2 sm:flex-row">
                <div className="flex flex-1 gap-2">
                  <select
                    aria-label="Land"
                    value={country}
                    onChange={(event) => setCountry(event.target.value as CountryCode)}
                    className="h-10 rounded-xl border border-input bg-background px-2 text-sm"
                  >
                    {COUNTRIES.map((c) => (
                      <option key={c.code} value={c.code}>
                        {c.flag} {c.dial}
                      </option>
                    ))}
                  </select>
                  <Input
                    id="guide-phone"
                    type="tel"
                    inputMode="tel"
                    autoComplete="tel"
                    value={phone}
                    onChange={(event) => setPhone(event.target.value)}
                    placeholder={`${activeCountry.dial} 151 23456789`}
                    aria-invalid={phoneError}
                    className="bg-background"
                  />
                </div>
                <Button type="submit" disabled={busy || !parsedPhone} className="shrink-0">
                  {done === "whatsapp" ? "WhatsApp geöffnet" : "Per WhatsApp anfordern"}
                </Button>
              </div>
              {phoneError && (
                <p className="mt-2 text-xs text-destructive">
                  Das sieht noch nicht nach einer gültigen Mobilnummer aus.
                </p>
              )}
            </form>
          ) : (
            <form
              className="mt-5"
              onSubmit={(event) => {
                event.preventDefault();
                void sendEmail();
              }}
            >
              <label htmlFor="guide-email" className="sr-only">
                E-Mail-Adresse
              </label>
              <div className="flex flex-col gap-2 sm:flex-row">
                <Input
                  id="guide-email"
                  type="email"
                  autoComplete="email"
                  value={email}
                  onChange={(event) => setEmail(event.target.value)}
                  placeholder="dein@salon.de"
                  className="bg-background"
                />
                <Button type="submit" disabled={busy} className="shrink-0">
                  {done === "email" ? "Angefordert" : "Guide anfordern"}
                </Button>
              </div>
              <p className="mt-3 text-xs text-muted-foreground">
                Wir melden uns mit dem Download-Link – kein Newsletter-Spam.
              </p>
            </form>
          )}

          {done && (
            <p className="mt-4 flex items-center gap-2 text-sm font-medium text-success">
              <Check className="size-4" /> Danke! Wir sind gleich bei dir.
            </p>
          )}

        </div>
      </div>
    </div>
  );
}
